import {Component, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {AssetActions} from "../../../../asset/store/asset.actions";
import {Store} from "@ngrx/store";
import {selectAssetArchivedDate, selectAssetDetailsCloseDialogDisabled, selectAssetIsArchived, selectAssetName} from "../../../../asset/store/asset/asset.selectors";
import {selectSelectedSourceDataset} from "../../../../import/store/import.selectors";
import {MatDialogRef} from "@angular/material/dialog";
import {map, Subscription} from "rxjs";
import {Router} from "@angular/router";
import {AssetValuationTypeEnum} from "../../../../asset/models/asset-valuation/asset-valuation-type.enum";

export enum assetDialogExpandType {
    GENERAL = "general",
    GPVALUATION = "gpvaluation",
    EXITINFO = "exitinfo",
    VALUATION = "valuation",
    PURCHASEMULTIPLES = "purchasemultiples"
}

@Component({
    selector: "valumize-asset-dmv-valuation-dialog",
    templateUrl: "./asset-details-dialog.component.html",
    styleUrls: ["./asset-details-dialog.component.scss"],
    standalone: false
})
export class AssetDetailsDialogComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly dialogRef = inject(MatDialogRef<AssetDetailsDialogComponent>);

    @Input() assetId?: number;
    @Input() fundId?: number;
    @Input() fundReportId?: number;
    @Input() fundValuationId?: number;
    @Input() preferredValuationType = AssetValuationTypeEnum.DiscountMultipleValuation;
    @Input() fundInvestmentId?: number;
    @Input() dealId?: number;
    @Input() expanded: assetDialogExpandType = assetDialogExpandType.VALUATION;
    subscriptions: Subscription[] = [];
    assetName$ = this.store.select(selectAssetName);
    isArchived$ = this.store.select(selectAssetIsArchived);
    archivedDate$ = this.store.select(selectAssetArchivedDate);
    sourceDataset$ = this.store.select(selectSelectedSourceDataset);
    isCloseDisabled$ = this.store.select(selectAssetDetailsCloseDialogDisabled);
    isEditDisabled = false;
    isCloseDisabled = false;
    isAssetDetailsDisabledInfo = false;
    protected readonly assetDialogExpandType = assetDialogExpandType;
    protected readonly valuationTypeEnum = AssetValuationTypeEnum;

    /**
     * Latches `true` the first time any sub-component form enters edit mode
     * during this dialog session. Passed back to the caller via
     * dialogRef.close(...) as the "should I refresh upstream data?" signal.
     *
     * Trade-off: clicking Edit then Cancel without saving still latches and
     * triggers a downstream reload. Acceptable per the explicit user
     * decision — the goal is "don't reload when the user only reads," and
     * "the user clicked Edit somewhere" is a robust enough proxy without
     * having to enumerate every save-success action.
     *
     * Archived assets: selectAssetDetailsCloseDialogDisabled returns
     * `false` for them (close stays enabled), so this never latches and
     * the close payload stays falsy — no needless reload for read-only
     * archived assets.
     */
    private didEnterEditMode = false;

    constructor() {
        this.dialogRef.disableClose = true; // Disable default close behavior
    }

    ngOnInit(): void {
        if (this.assetId) {
            this.store.dispatch(AssetActions.loadAssetData({
                assetId: this.assetId,
                fundId: this.fundId,
                fundReportId: this.fundReportId,
                fundValuationId: this.fundValuationId,
                dealId: this.dealId
            }));
        }

        this.subscriptions.push(
            this.isCloseDisabled$.pipe(map((isDisabled) => {
                this.dialogRef.disableClose = isDisabled;
                this.isEditDisabled = isDisabled;
                if (isDisabled) {
                    this.didEnterEditMode = true;
                } else {
                    this.isCloseDisabled = false;
                    this.isAssetDetailsDisabledInfo = false;
                }
            })).subscribe());
    }

    closeDialog(): void {
        if (!this.isEditDisabled) {
            this.dialogRef.close(this.didEnterEditMode);
        } else {
            this.isCloseDisabled = true;
        }
    }

    openAssetDetails(): void {
        if (!this.isEditDisabled) {
            this.dialogRef.close();
            this.router.navigate(["../../assets/", this.assetId], {
                queryParams: {
                    fundId: this.fundId,
                    fundReportId: this.fundReportId,
                    fundValuationId: this.fundValuationId,
                    dealId: this.dealId
                }
            });
        } else {
            this.isAssetDetailsDisabledInfo = true;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
